/**
 * Short-lived client-side gate for the V2 Route (`/v2`), stored in
 * localStorage. Expected to be deleted once V2 is enabled for everyone.
 *
 * Testers opt in with `?v2=1` (`?v2=0` to opt out); the query param is
 * consumed at page load and stripped from the URL. When localStorage
 * is unavailable, the flag reads as disabled -> Legacy Route.
 */

const V2_ENABLED_KEY = "dacw:v2Enabled";
const OPT_IN_QUERY_PARAM = "v2";

// Any value not in these sets is treated as "no change" so a stray
// `?v2=` in a copy-pasted link cannot silently revoke an opt-in.
const OPT_IN_VALUES = new Set(["1", "true"]);
const OPT_OUT_VALUES = new Set(["0", "false"]);

/**
 * Read `?v2=...` from the current URL, persist to localStorage, and
 * strip the param via `history.replaceState`. Idempotent no-op when
 * the param is absent, unrecognized, or when storage/history APIs
 * are unavailable. Called from `main.tsx` once before React mounts.
 */
export function applyV2OptInFromUrl(): void {
  if (typeof window === "undefined") return;
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }
  const value = url.searchParams.get(OPT_IN_QUERY_PARAM);
  if (value === null) return;

  const normalized = value.toLowerCase();
  try {
    if (OPT_IN_VALUES.has(normalized)) {
      window.localStorage.setItem(V2_ENABLED_KEY, "true");
    } else if (OPT_OUT_VALUES.has(normalized)) {
      window.localStorage.removeItem(V2_ENABLED_KEY);
    } else {
      return; // unrecognized value: leave storage and URL alone
    }
  } catch {
    // storage blocked - still strip the URL param so testers do not
    // see a confusing `?v2=1` linger after clicking the bookmark.
  }

  url.searchParams.delete(OPT_IN_QUERY_PARAM);
  try {
    window.history.replaceState(null, "", url.toString());
  } catch {
    // history API missing (very old browsers): non-fatal.
  }
}

/** Synchronous V2 Route gate. Storage errors resolve to `false`. */
export function isV2Enabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(V2_ENABLED_KEY) === "true";
  } catch {
    return false;
  }
}
